// src/utils/fieldTransformer.ts

import { BusinessDetails, ContactDetails, OwnerDetails, RegistrationData } from "../types/registration";

// Define the mapping between database fields and form fields
const DB_TO_FORM_MAPPING = {
  // Contact Details
  email: 'email',
  phone: 'mobile',
  relationshil_manager: 'relationshipManager', // assuming typo in your DB field
  
  // Business Details  
  masked_pan_no: 'panNumber',
  name: 'brandName', // assuming this maps to brand name
  type: 'businessType',
  address_id: 'operatingAddress',
  
  // Owner Details
  masked_aadhar_no: 'aadhaarNumber',
  dob: 'dateOfBirth', // if you add this to your schema
  
  // You can extend this mapping as needed
} as const;

// Create reverse mapping for form to DB
const FORM_TO_DB_MAPPING = Object.fromEntries(
  Object.entries(DB_TO_FORM_MAPPING).map(([dbField, formField]) => [formField, dbField])
);

// Generic transformer function
export class FieldTransformer {
  /**
   * Transform database object to form object
   */
  static dbToForm<T extends Record<string, any>>(dbData: Record<string, any>): Partial<T> {
    const transformed: any = {};
    
    for (const [dbField, value] of Object.entries(dbData)) {
      const formField = DB_TO_FORM_MAPPING[dbField as keyof typeof DB_TO_FORM_MAPPING];
      if (formField && value !== undefined && value !== null) {
        transformed[formField] = value;
      }
    }
    
    return transformed;
  }

  /**
   * Transform form object to database object
   */
  static formToDb<T extends Record<string, any>>(formData: T): Record<string, any> {
    const transformed: any = {};
    
    for (const [formField, value] of Object.entries(formData)) {
      const dbField = FORM_TO_DB_MAPPING[formField];
      if (dbField && value !== undefined && value !== null) {
        transformed[dbField] = value;
      }
    }
    
    return transformed;
  }

  /**
   * Transform specific section data
   */
  static transformContactDetails(dbData: Record<string, any>): Partial<ContactDetails> {
    return {
      email: dbData.email,
      mobile: dbData.phone,
      relationshipManager: dbData.relationshil_manager,
      // Add other mappings as needed
    };
  }

  static transformBusinessDetails(dbData: Record<string, any>): Partial<BusinessDetails> {
    return {
      panNumber: dbData.masked_pan_no,
      brandName: dbData.name,
      businessType: dbData.type as 'business' | 'individual',
      operatingAddress: dbData.address_id,
      // Add other mappings as needed
    };
  }

  static transformOwnerDetails(dbData: Record<string, any>): Partial<OwnerDetails> {
    return {
      panNumber: dbData.masked_pan_no,
      aadhaarNumber: dbData.masked_aadhar_no,
      // Add other mappings as needed
    };
  }
}

// Usage example for updating forms
export const updateFormWithDbData = (
  dbData: Record<string, any>,
  currentFormData: RegistrationData
): RegistrationData => {
  return {
    contactDetails: {
      ...currentFormData.contactDetails,
      ...FieldTransformer.transformContactDetails(dbData),
    },
    businessDetails: {
      ...currentFormData.businessDetails,
      ...FieldTransformer.transformBusinessDetails(dbData),
    },
    ownerDetails: {
      ...currentFormData.ownerDetails,
      ...FieldTransformer.transformOwnerDetails(dbData),
    },
    productSelection: currentFormData.productSelection,
  };
};